import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Eye, Users, Package } from 'lucide-react';
type NotificationType = 'purchase' | 'viewing' | 'viewers_count' | 'stock';
type Notification = {
  id: number;
  type: NotificationType;
  message: string;
  icon: React.ReactNode;
  accentColor: string;
};
const names = ['María', 'Carmen', 'Isabel', 'Ana', 'Laura', 'Sofía', 'Elena', 'Paula', 'Lucía', 'Marta', 'Rosa', 'Pilar'];
const cities = ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Málaga', 'Bilbao', 'Zaragoza', 'Murcia', 'Alicante'];
const packs = [1, 2, 3, 6];
const viewerCounts = [12, 18, 23, 31, 27, 19, 24, 16];
const stockCounts = [7, 12, 9, 15, 8, 11];
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function generateNotification(id: number): Notification {
  const types: NotificationType[] = ['purchase', 'viewing', 'viewers_count', 'stock'];
  const type = getRandomItem(types);
  const name = getRandomItem(names);
  const city = getRandomItem(cities);
  const pack = getRandomItem(packs);
  const viewers = getRandomItem(viewerCounts);
  const stock = getRandomItem(stockCounts);
  const packLabel = pack === 1 ? '1 mes' : `${pack} meses`;
  switch (type) {
    case 'purchase':
      return {
        id,
        type,
        message: `${name} de ${city} acaba de comprar el Pack de ${packLabel}`,
        icon: <ShoppingBag className="w-5 h-5" data-id="element-164" />,
        accentColor: 'bg-green-500'
      };
    case 'viewing':
      return {
        id,
        type,
        message: `${name} de ${city} está viendo el Pack de ${packLabel}`,
        icon: <Eye className="w-5 h-5" data-id="element-165" />,
        accentColor: 'bg-blue-500'
      };
    case 'viewers_count':
      return {
        id,
        type,
        message: `${viewers} personas están viendo esta página ahora`,
        icon: <Users className="w-5 h-5" data-id="element-166" />,
        accentColor: 'bg-purple-500'
      };
    case 'stock':
      return {
        id,
        type,
        message: `¡Solo quedan ${stock} unidades del Pack más popular!`,
        icon: <Package className="w-5 h-5" data-id="element-167" />,
        accentColor: 'bg-orange-500'
      };
  }
}
export function LiveActivityNotifications() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [notificationId, setNotificationId] = useState(0);
  useEffect(() => {
    // Initial delay before first notification
    const initialDelay = setTimeout(() => {
      showNewNotification();
    }, 5000);
    return () => clearTimeout(initialDelay);
  }, []);
  const showNewNotification = () => {
    const newId = notificationId + 1;
    setNotificationId(newId);
    setNotification(generateNotification(newId));
    // Hide notification after 4-5 seconds
    const hideDelay = 4000 + Math.random() * 1000;
    setTimeout(() => {
      setNotification(null);
      // Show next notification after 8-15 seconds
      const nextDelay = 8000 + Math.random() * 7000;
      setTimeout(showNewNotification, nextDelay);
    }, hideDelay);
  };
  return <div className="fixed bottom-4 left-4 z-50 max-w-[320px]" data-id="element-168">
      <AnimatePresence mode="wait" data-id="element-169">
        {notification && <motion.div key={notification.id} initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 30
      }} className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden" data-id="element-170">
            <div className="flex items-start gap-3 p-4" data-id="element-171">
              {/* Icon with accent color */}
              <div className={`${notification.accentColor} text-white p-2 rounded-lg flex-shrink-0`} data-id="element-172">
                {notification.icon}
              </div>

              {/* Message */}
              <div className="flex-1 min-w-0" data-id="element-173">
                <p className="text-sm font-medium text-gray-900 leading-snug" data-id="element-174">
                  {notification.message}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-175">Hace unos segundos</p>
              </div>
            </div>

            {/* Progress bar */}
            <motion.div initial={{
          scaleX: 1
        }} animate={{
          scaleX: 0
        }} transition={{
          duration: 4.5,
          ease: 'linear'
        }} className={`h-1 ${notification.accentColor} origin-left`} data-id="element-176" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}